import { getPredictions, updatePredictionOutcome } from '../services/storage';

// ─── JARVIS Accuracy Calculator ───────────────────────────────────────
// Reads all logged predictions and calculates how accurate JARVIS is.
// This is the core of JARVIS self-awareness — it knows where it's right
// and where it's wrong, and improves over time.

export const calcJarvisAccuracy = () => {
  const predictions = getPredictions().filter(p => p.outcome_recorded);

  if (!predictions.length) {
    return {
      overall: 0,
      correct: 0,
      partial: 0,
      wrong: 0,
      total: 0,
      bySession: {},
      byGrade: {},
      byDirection: {},
      weeklyTrend: [],
      bestArea: 'Collecting data...',
      weakestArea: 'Collecting data...',
    };
  }

  const correct = predictions.filter(p => p.was_correct === 'CORRECT').length;
  const partial = predictions.filter(p => p.was_correct === 'PARTIAL').length;
  const wrong   = predictions.filter(p => p.was_correct === 'WRONG').length;

  // Weighted accuracy: CORRECT=1, PARTIAL=0.5, WRONG=0
  const weightedScore = ((correct + partial * 0.5) / predictions.length) * 100;

  // By session
  const bySession = {};
  predictions.forEach(p => {
    const s = p.session || 'Unknown';
    if (!bySession[s]) bySession[s] = { correct: 0, total: 0 };
    bySession[s].total++;
    if (p.was_correct === 'CORRECT') bySession[s].correct++;
    if (p.was_correct === 'PARTIAL') bySession[s].correct += 0.5;
  });
  Object.keys(bySession).forEach(s => {
    bySession[s].rate = Math.round((bySession[s].correct / bySession[s].total) * 100);
  });

  // By grade
  const byGrade = {};
  predictions.forEach(p => {
    const g = p.grade || 'Unknown';
    if (!byGrade[g]) byGrade[g] = { correct: 0, total: 0 };
    byGrade[g].total++;
    if (p.was_correct === 'CORRECT') byGrade[g].correct++;
  });
  Object.keys(byGrade).forEach(g => {
    byGrade[g].rate = Math.round((byGrade[g].correct / byGrade[g].total) * 100);
  });

  // By direction
  const byDirection = {};
  predictions.forEach(p => {
    const d = p.direction || 'Unknown';
    if (!byDirection[d]) byDirection[d] = { correct: 0, total: 0 };
    byDirection[d].total++;
    if (p.was_correct === 'CORRECT') byDirection[d].correct++;
  });
  Object.keys(byDirection).forEach(d => {
    byDirection[d].rate = Math.round((byDirection[d].correct / byDirection[d].total) * 100);
  });

  // Weekly trend (last 8 weeks)
  const weeklyTrend = buildWeeklyTrend(predictions);

  // Best and weakest areas
  const sessionRates  = Object.entries(bySession).map(([k, v]) => ({ name: k, rate: v.rate }));
  const bestSession   = sessionRates.sort((a, b) => b.rate - a.rate)[0]?.name;
  const weakestSession = sessionRates.sort((a, b) => a.rate - b.rate)[0]?.name;

  return {
    overall:    Math.round(weightedScore),
    correct,
    partial,
    wrong,
    total:      predictions.length,
    bySession,
    byGrade,
    byDirection,
    weeklyTrend,
    bestArea:    bestSession    ? `${bestSession} session` : 'Collecting data...',
    weakestArea: weakestSession ? `${weakestSession} session` : 'Collecting data...',
  };
};

// ─── Weekly trend builder ─────────────────────────────────────────────
const buildWeeklyTrend = (predictions) => {
  const weeks = {};

  predictions.forEach(p => {
    if (!p.date) return;
    const d    = new Date(p.date);
    // Get Monday of this week as the key
    const day  = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    const key  = monday.toISOString().split('T')[0];

    if (!weeks[key]) weeks[key] = { correct: 0, total: 0 };
    weeks[key].total++;
    if (p.was_correct === 'CORRECT') weeks[key].correct++;
    if (p.was_correct === 'PARTIAL') weeks[key].correct += 0.5;
  });

  return Object.entries(weeks)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-8)
    .map(([week, data]) => ({
      week,
      accuracy: Math.round((data.correct / data.total) * 100),
      total: data.total,
    }));
};

// ─── Outcome recorder ─────────────────────────────────────────────────
// Called when Sir tells us what actually happened after a JARVIS prediction.
export const recordPredictionOutcome = (predictionId, { wasCorrect, actualOutcome, linkedTrade = null }) => {
  updatePredictionOutcome(predictionId, {
    was_correct:    wasCorrect,   // 'CORRECT' | 'PARTIAL' | 'WRONG'
    actual_outcome: actualOutcome,
    linked_trade:   linkedTrade,
  });
};

// ─── Evolution stage calculator ───────────────────────────────────────
// Stage grows based on: discussion count + accuracy + data volume
export const calcEvolutionStage = (jarvisMemory, accuracy) => {
  const discussions = jarvisMemory.total_discussions || 0;

  if (discussions >= 500 && accuracy >= 85) return 5; // Partner
  if (discussions >= 300 && accuracy >= 80) return 4; // Advisor
  if (discussions >= 150 && accuracy >= 72) return 3; // Analyst
  if (discussions >= 50  && accuracy >= 63) return 2; // Learner
  return 1;                                            // Student
};

// Stage labels — used in UI display
export const EVOLUTION_STAGES = {
  1: { name: 'Student',  desc: 'Learning the basics from Sir',           color: '#7ab3cc', accuracy: '55–65%' },
  2: { name: 'Learner',  desc: 'Connecting rules and patterns',          color: '#00d4ff', accuracy: '65–75%' },
  3: { name: 'Analyst',  desc: 'Full knowledge base, seeing confluence', color: '#ffaa00', accuracy: '75–83%' },
  4: { name: 'Advisor',  desc: 'Predicting before Sir asks',             color: '#00ff88', accuracy: '83–89%' },
  5: { name: 'Partner',  desc: 'Institutional-level market intelligence',color: '#ffd700', accuracy: '89%+'   },
};
